import {
  ArrowUpDown,
  CalendarDays,
  Clock,
  FileTextIcon,
  GraduationCap,
  Info,
} from "lucide-react";
import type { ReactNode } from "react";
import { formatDanishLongDate } from "@/util/date";
import RoundedCard from "@cle/components/RoundedCard";
import { REQUIRED_PERIOD_LESSONS } from "@cle/constants/lessonRequirements";
import { useReportingLessonsStatus } from "@cle/hooks/useReportingLessonsStatus";
import type { ReportingPeriod } from "@cle/models/reportingPeriod";
import { buildCurrentPeriodLessonsStatus } from "@cle/utils/currentPeriodLessonsStatus";
import { formatLessons } from "@cle/utils/lessonLimits";
import ReportPeriodStatusBadge from "./components/ReportPeriodStatusBadge";

const MISSING_VALUE = "-";

type PeriodStateCopy = {
  badgeClassName: string;
  badgeLabel: string;
  description: string;
  infoText: string;
  periodEndLabel: string;
};

const CURRENT_PERIOD_COPY: PeriodStateCopy = {
  badgeClassName: "cle-report-detail__reported-status--info",
  badgeLabel: "Nuværende periode",
  description:
    "Denne periode er stadig i gang. Her kan du se de vigtigste oplysninger om perioden, før den bliver klar til indberetning.",
  infoText:
    "Indberetning åbner først, når perioden er udløbet. Indtil da kan du løbende uploade kursusbeviser på forsiden under Hjem — lektionstallet opdateres her, efterhånden som dine kursusbeviser bliver behandlet.",
  periodEndLabel: "Indberetning åbner",
};

const ENDED_PERIOD_COPY: PeriodStateCopy = {
  badgeClassName: "cle-report-detail__reported-status--warning",
  badgeLabel: "Afventer indberetning",
  description:
    "Perioden er udløbet, men indberetningen er endnu ikke åben. Her kan du se de vigtigste oplysninger om perioden.",
  infoText:
    "Indberetningen for perioden er ikke åbnet endnu. Du får besked, når du kan indberette dine lektioner.",
  periodEndLabel: "Perioden sluttede",
};

type CurrentIndberetningDetailViewProps = {
  isCurrentPeriod: boolean;
  reportingPeriod: ReportingPeriod;
};

const formatPeriodRange = (reportingPeriod: ReportingPeriod) => {
  return `${formatDanishLongDate(
    reportingPeriod.periodFromDate,
    MISSING_VALUE,
  )} - ${formatDanishLongDate(
    reportingPeriod.periodToDateMinusOneDay,
    MISSING_VALUE,
  )}`;
};

const CurrentPeriodMetric = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) => (
  <div className="cle-report-detail__reported-metric">
    <span className="cle-report-detail__reported-metric-icon">{icon}</span>
    <span>
      <span className="cle-report-detail__reported-metric-label">{label}</span>
      <span className="cle-report-detail__reported-metric-value">{value}</span>
    </span>
  </div>
);

const CurrentIndberetningDetailView = ({
  isCurrentPeriod,
  reportingPeriod,
}: CurrentIndberetningDetailViewProps) => {
  const copy = isCurrentPeriod ? CURRENT_PERIOD_COPY : ENDED_PERIOD_COPY;
  const periodRange = formatPeriodRange(reportingPeriod);
  const periodEndsAt = formatDanishLongDate(
    reportingPeriod.periodToDateMinusOneDay,
    MISSING_VALUE,
  );
  const transferredLessons = reportingPeriod.transferredFromPreviousPeriod ?? 0;

  // periodToDate er periodens EKSKLUSIVE slutdato, og det er den, endepunktet forventer.
  // Sendes periodToDateMinusOneDay i stedet, tabes et kursus dateret paa sidste dag i
  // perioden — uden fejl, blot et lidt for lavt tal.
  const {
    data: lessonsStatus,
    isError: isLessonsError,
    isLoading: isLoadingLessons,
  } = useReportingLessonsStatus({
    periodStart: reportingPeriod.periodFromDate,
    periodEnd: reportingPeriod.periodToDate,
  });

  // Tallet taeller overfoerte lektioner med, praecis som forsiden og som det kort,
  // der dukker op, naar indberetningen aabner. Feltet "Overfoert fra sidste periode"
  // ved siden af viser, hvor stor en del af det tal der er overfoert.
  const { detailValueText } = buildCurrentPeriodLessonsStatus({
    availableLessons: lessonsStatus?.availableLessons,
    lessonsKnown: !isLessonsError,
    periodFromDate: reportingPeriod.periodFromDate,
    periodToDateMinusOneDay: reportingPeriod.periodToDateMinusOneDay,
    transferredLessons,
  });

  return (
    <div className="cle-report-detail">
      <section className="cle-report-detail__reported-shell">
        <div className="cle-report-detail__reported-hero cle-report-detail__reported-hero-info">
          <div className="cle-report-detail__reported-hero-content">
            <p className="cle-afventende-indberetninger-card__subtitle mb-0">
              Efteruddannelsesperiode
            </p>
            <h1 className="cle-report-detail__reported-title">{periodRange}</h1>

            <p className="cle-report-detail__reported-description">
              {copy.description}
            </p>
          </div>
          <ReportPeriodStatusBadge
            className={copy.badgeClassName}
            icon={<Clock size={16} aria-hidden strokeWidth={2} />}
            label={copy.badgeLabel}
          />
        </div>

        <RoundedCard className="cle-report-detail__reported-summary">
          <div className="cle-report-detail__reported-summary-header">
            <div>
              <h2 className="cle-report-detail__reported-period mb-0">
                Information om perioden
              </h2>
              <p className="cle-afventende-indberetninger-card__subtitle mb-1">
                {periodRange}
              </p>
            </div>
          </div>

          <div className="cle-report-detail__reported-metrics">
            <CurrentPeriodMetric
              icon={<CalendarDays size={18} aria-hidden strokeWidth={2} />}
              label={copy.periodEndLabel}
              value={periodEndsAt}
            />
            <CurrentPeriodMetric
              icon={<FileTextIcon size={18} aria-hidden strokeWidth={2} />}
              label="Lektionskrav"
              value={`${REQUIRED_PERIOD_LESSONS} lektioner`}
            />
            <CurrentPeriodMetric
              icon={<GraduationCap size={18} aria-hidden strokeWidth={2} />}
              label="Lektioner i alt"
              value={
                isLoadingLessons ? (
                  <span className="placeholder-glow">
                    <span
                      className="placeholder rounded"
                      style={{ height: "1rem", width: "4rem" }}
                    />
                  </span>
                ) : (
                  detailValueText
                )
              }
            />
            <CurrentPeriodMetric
              icon={<ArrowUpDown size={18} aria-hidden strokeWidth={2} />}
              label="Overført fra sidste periode"
              value={`${formatLessons(transferredLessons)} lektioner`}
            />
          </div>
        </RoundedCard>

        <RoundedCard>
          <div className="cle-report-detail__reported-info mt-0">
            <Info size={18} aria-hidden strokeWidth={2} />
            <p className="mb-0">{copy.infoText}</p>
          </div>
        </RoundedCard>
      </section>
    </div>
  );
};

export default CurrentIndberetningDetailView;
